<template>
  <b-card>
    <h1>Total Patients by Insurance</h1>
    <b-form-group label="Select Insurance">
      <b-form-select
        v-model="selectedInsurance"
        :options="insuranceOptions"
        @change="filterData"></b-form-select>
    </b-form-group>
    <vue-apex-charts :options="chartOptions" :series="series" type="bar" />
    <b-button variant="primary" @click="downloadData">Download Data</b-button>
  </b-card>
</template>

<script>
import { BCard, BFormGroup, BFormSelect, BButton } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'

export default {
  components: {
    BCard,
    BFormGroup,
    BFormSelect,
    BButton,
    VueApexCharts,
  },
  props: {
    dataDashboard: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedInsurance: 'all', // Default value to show all data
      insuranceOptions: [], // Options for the select dropdown
      chartOptions: {
        chart: {
          type: 'bar',
        },
        xaxis: {
          categories: [],
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '95%', // Reduce el ancho de las barras para separarlas
            barHeight: '80%', // Opcional: ajusta la altura si es necesario
          },
        },
        dataLabels: {
          enabled: true, // Asegura que las etiquetas estén habilitadas
          style: {
            colors: ['#000'], // Color de las etiquetas para que sean visibles
          },
          formatter: val => (val > 0 ? val : ''), // Muestra solo valores mayores a 0
        },
        tooltip: {
          y: {
            formatter: val => `${val} patients`,
          },
        },
      },
      series: [],
    }
  },
  methods: {
    initializeInsuranceOptions() {
      // Generate unique options for the select dropdown from the data
      this.insuranceOptions = [
        { value: 'all', text: 'All Insurances' }, // Option to show all
        ...this.dataDashboard.all_patients_by_insurance.map(insurance => ({
          value: insurance.type_insurance,
          text: insurance.type_insurance,
        })),
      ]
    },
    filterData() {
      const filteredData =
        this.selectedInsurance === 'all'
          ? this.dataDashboard.all_patients_by_insurance
          : this.dataDashboard.all_patients_by_insurance.filter(
              insurance => insurance.type_insurance === this.selectedInsurance
            )

      this.updateChart(filteredData)
    },
    updateChart(data) {
      const categories = []
      const npData = []
      const ncData = []

      // Procesar los datos para extraer categorías y valores
      data.forEach(insurance => {
        categories.push(
          insurance.type_insurance
            ? insurance.type_insurance.length > 13
              ? insurance.type_insurance.substring(0, 13) + '...'
              : insurance.type_insurance
            : ''
        ) // Agregar el tipo de seguro como categoría

        // Contar pacientes por tipo de membresía
        const npCount = insurance.patients.filter(
          patient => patient.type_membership === 'NP'
        ).length
        const ncCount = insurance.patients.filter(
          patient => patient.type_membership === 'NC'
        ).length

        npData.push(npCount) // Agregar el conteo de nuevos pacientes
        ncData.push(ncCount) // Agregar el conteo de pacientes recurrentes
      })

      // Actualizar las categorías del eje X
      this.chartOptions = {
        ...this.chartOptions, // Mantener las configuraciones existentes
        xaxis: {
          ...this.chartOptions.xaxis,
          categories, // Actualizar las categorías
        },
      }

      // Actualizar las series de datos
      this.series = [
        { name: 'New Patients (NP)', data: npData },
        { name: 'Returning Patients (NC)', data: ncData },
      ]
    },
    downloadData() {
      // Data for the first sheet
      const summaryData = this.dataDashboard.all_patients_by_insurance.map(insurance => ({
        Insurance: insurance.type_insurance,
        'New Patients (NP)': insurance.patients.filter(
          patient => patient.type_membership === 'NP'
        ).length,
        'Returning Patients (NC)': insurance.patients.filter(
          patient => patient.type_membership === 'NC'
        ).length,
      }))

      // Data for additional sheets (patients by clinic)
      const clinicData = this.dataDashboard.all_patients_by_insurance.map(insurance => ({
        clinic: insurance.type_insurance,
        patients: insurance.patients.map(patient => ({
          ID: patient.id,
          Name: patient.name,
          Lastname: patient.lastname,
          Gender: patient.gender,
          Membership: patient.type_membership,
          Phone: patient.phone,
          Email: patient.email,
          Address: patient.address,
        })),
      }))

      this.exportToCsv('patients_by_insurance.csv', summaryData, clinicData)
    },
    exportToCsv(filename, summaryRows, clinicData) {
      // Process the first sheet (summary)
      const processRow = row =>
        Object.values(row)
          .map(value => `"${value}"`)
          .join(',')

      let csvContent =
        'data:text/csv;charset=utf-8,' +
        [Object.keys(summaryRows[0]).join(','), ...summaryRows.map(processRow)].join('\n')

      // Process additional sheets (clinics)
      clinicData.forEach(clinic => {
        csvContent += `\n\nClinic: ${clinic.clinic}\n`
        if (clinic.patients.length > 0) {
          csvContent += [
            Object.keys(clinic.patients[0]).join(','),
            ...clinic.patients.map(processRow),
          ].join('\n')
        } else {
          csvContent += 'No patients found\n'
        }
      })

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },
  watch: {
    dataDashboard: {
      immediate: true,
      handler(newData) {
        this.initializeInsuranceOptions()
        this.updateChart(newData.all_patients_by_insurance)
      },
    },
  },
}
</script>
